import React from 'react'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PollCheckDecoration from './PollCheckDecoration'
import { ShareButton } from './ShareButton'
import { Poll } from 'src/api/types/polls'

export default function PollDetailsHero({
  poll,
  voting,
  isOwner,
  canViewResults,
  onPlay,
}: {
  poll: Poll
  voting: boolean
  isOwner: boolean
  canViewResults: boolean
  onPlay?: (poll: Poll) => void
}) {
  const canPlay = canViewResults && poll.state === 'results_open' && !!poll.submissions
  return (
    <Box
      className="rt-poll-hero"
      sx={{
        p: { xs: 2, sm: 3 },
        background: '#ffefdd',
        border: '1px solid #efddc9',
        borderRadius: '28px',
        boxShadow: '0 4px 0 #e8bb99',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        textAlign: 'left',
        '& .MuiTypography-root': { textAlign: 'left' },
        '& > :not(.rt-poll-decoration)': { position: 'relative', zIndex: 1 },
      }}
    >
      <PollCheckDecoration />
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={1.5}>
        <Typography
          variant="overline"
          sx={{ color: '#855c3d', letterSpacing: 1.2, fontWeight: 700 }}
        >
          {voting ? 'YOUR RANKING VOTE' : 'COMMUNITY POLL'}
        </Typography>
        {isOwner && (
          <Chip size="small" label="Your poll" sx={{ background: '#fffaf5', color: '#693413' }} />
        )}
        {poll.dailyPollType && (
          <Chip
            size="small"
            label={poll.dailyPollType === 'today' ? 'Today’s Poll' : 'Yesterday’s Results'}
            sx={{ background: '#fffaf5', color: '#693413' }}
          />
        )}
        {!canViewResults && (
          <Chip
            size="small"
            label="Results Hidden"
            sx={{ background: '#ffe4e1', color: '#9d4043' }}
          />
        )}
      </Stack>
      <Box display="flex" gap={{ xs: 1.5, sm: 2 }} alignItems="center">
        <Box
          component="img"
          src="/brand/random-poll.svg"
          alt=""
          aria-hidden
          sx={{ width: { xs: 56, sm: 84 }, flexShrink: 0 }}
        />
        <Box minWidth={0}>
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: 27, sm: 36 }, color: '#693413', overflowWrap: 'anywhere' }}
          >
            {poll.prompt}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: '#855c3d' }}>
            {voting
              ? 'Put your favorites first. Your opinion shapes the ranking.'
              : 'Your opinion. Everyone’s picks. A challenge to try.'}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 2 }}
      >
        {[
          ['Votes', poll.submissions || 0],
          ['Plays', poll.plays || 0],
          ['Options', poll.items?.length || 0],
        ].map(([label, value]) => (
          <Box
            key={label}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: '14px',
              background: '#fffaf5',
              border: '1px solid #efddc9',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Nunito", sans-serif',
                fontWeight: 800,
                fontSize: 20,
                color: '#693413',
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#855c3d' }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mt={2}>
        {canPlay && onPlay && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => onPlay(poll)}
            endIcon={<PlayArrowRoundedIcon />}
            sx={{ minHeight: 44, fontWeight: 700 }}
          >
            {voting ? 'Skip to Play' : 'Play Poll'}
          </Button>
        )}
        <ShareButton
          shareType="poll"
          pollId={poll.id}
          pollShareMode="vote"
          listTitle={poll.prompt}
          variant="outlined"
          sx={{
            color: '#944217',
            background: '#fffaf5',
            boxShadow: 'none',
            borderColor: '#e8cdb2',
          }}
        />
        {poll.stats?.controversy && !voting && (
          <Chip
            size="small"
            label={`${poll.stats.controversy} opinions`}
            sx={{ background: '#fffaf5', color: '#855c3d', textTransform: 'capitalize' }}
          />
        )}
      </Box>
    </Box>
  )
}
